/** perf_sample's frame measures (docs/metrics.md): seconds spent at each frame rate, and frame time. */
export interface FrameSample {
  fps_lt20: number;
  fps_20_29: number;
  fps_30_44: number;
  fps_45_54: number;
  fps_ge55: number;
  /** 95th-percentile interval between display frames over the sample (ms). */
  p95_frame_ms: number;
}

/**
 * Measures display frames for perf_sample. Each wall-clock second is bucketed by how many frames it
 * drew, so a stall shows up as slow seconds. A hidden tab draws nothing at all: `pause` drops the
 * second in progress instead of counting the hidden time as slow.
 */
export class PerfSampler {
  /** Start of the second being counted (null when paused or not started). */
  #second: number | null = null;
  #frames = 0;
  #last: number | null = null;
  #intervals: number[] = [];
  #buckets = [0, 0, 0, 0, 0];

  /** A frame was drawn at `now` (ms, the display loop's clock). */
  frame(now: number): void {
    if (this.#last !== null) this.#intervals.push(now - this.#last);
    this.#last = now;
    this.#second ??= now;
    while (now - this.#second >= 1000) {
      const f = this.#frames;
      const bucket = f < 20 ? 0 : f < 30 ? 1 : f < 45 ? 2 : f < 55 ? 3 : 4;
      this.#buckets[bucket] = (this.#buckets[bucket] ?? 0) + 1;
      this.#frames = 0;
      this.#second += 1000;
    }
    this.#frames++;
  }

  /** The tab was hidden: frames stop until it's shown again. */
  pause(): void {
    this.#second = null;
    this.#last = null;
    this.#frames = 0;
  }

  /** The sample since the last one, or null if not a whole second was measured. Starts the next. */
  take(): FrameSample | null {
    const [lt20 = 0, b20 = 0, b30 = 0, b45 = 0, ge55 = 0] = this.#buckets;
    if (lt20 + b20 + b30 + b45 + ge55 === 0) return null;
    const sorted = [...this.#intervals].sort((a, b) => a - b);
    const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] ?? 0;
    this.#buckets = [0, 0, 0, 0, 0];
    this.#intervals = [];
    return {
      fps_lt20: lt20,
      fps_20_29: b20,
      fps_30_44: b30,
      fps_45_54: b45,
      fps_ge55: ge55,
      p95_frame_ms: Math.round(p95 * 10) / 10,
    };
  }
}
